import { Subject, Observable, of, from } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

/**
 * Convert object to url params.
 * @param validatedParams Query Params to convert
 */
export function paramterize(validatedParams: Record<string, any>) {
  return new URLSearchParams(
    Object.entries(validatedParams).filter(([, value]) =>
      typeof value === 'string' ? value.trim() : value
    )
  );
}

/**
 * Pick `data` from `AxiosResponse`.
 */
export const pickResponse = <T extends { data?: any }>() => map<T, T['data']>(res => res.data);

/**
 * Pick `data` from `BaseResponse`.
 */
export const pickResult = <T extends { data?: any }>() =>
  map<T, T['data']['data']>(config => config.data.data);

/**
 * Pick `data` from a `PaginatedResponse`.
 */
export const pickPaginated = <T extends { data?: any }>() =>
  map<T, T['data']['data']['data']>(config => config.data.data.data);

/**
 * Creates a response cachable api.
 * NOTE: Does not takes parameters into consideration. Should be used only
 * when the request does not have any params or has fixed params.
 * @param requester The function whose response to cache.
 */
export function cachedApi<K extends (...a: any) => Observable<any>>(requester: K): K {
  return (() => {
    let cached: any;

    let running: Promise<any>;

    async function runner(...args: any) {
      if (!running) {
        running = requester(...args).toPromise();
      }
      cached = await running;
      return cached;
    }

    return (...a: any) => {
      if (cached) return of(cached);
      return of(null).pipe(switchMap(() => runner(...a)));
    };
  })() as K;
}
